/**
 * Toast notifications: an imperative API usable anywhere (`toast.success("Saved")`,
 * also from non-React code such as the query client) plus the `useToast()` hook.
 * Rendered by `<Toaster />` (mounted once in the app root).
 *
 * Long-running work (jobs) can keep one toast and update it:
 *   const id = toast.show({ title: 'Exporting…', progress: 0, duration: null });
 *   toast.update(id, { progress: 0.5 });  …  toast.update(id, { tone: 'success', title: 'Done', progress: undefined, duration: 4000 });
 */
import { create } from 'zustand';
import { errorMessage } from '@/api/client';

export type ToastTone = 'info' | 'success' | 'warning' | 'error';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  title: string;
  description?: string;
  tone?: ToastTone;
  /** Auto-dismiss after ms; `null` keeps it until dismissed (default 5 s, errors 8 s). */
  duration?: number | null;
  /** 0..1 shows a progress bar; `null` an indeterminate one; `undefined` none. */
  progress?: number | null;
  action?: ToastAction;
}

export interface ToastItem extends Required<Pick<ToastOptions, 'title' | 'tone'>> {
  id: string;
  description: string | undefined;
  duration: number | null;
  progress: number | null | undefined;
  action: ToastAction | undefined;
  /** Bumped on every update so the auto-dismiss timer restarts. */
  revision: number;
}

interface ToastState {
  toasts: ToastItem[];
}

const MAX_TOASTS = 5;
let counter = 0;

export const useToastStore = create<ToastState>()(() => ({ toasts: [] }));

function defaultDuration(tone: ToastTone): number {
  return tone === 'error' ? 8000 : 5000;
}

function show(options: ToastOptions): string {
  const tone = options.tone ?? 'info';
  const item: ToastItem = {
    id: `t${++counter}`,
    title: options.title,
    description: options.description,
    tone,
    duration: options.duration === undefined ? defaultDuration(tone) : options.duration,
    progress: options.progress,
    action: options.action,
    revision: 0,
  };
  useToastStore.setState((state) => ({ toasts: [...state.toasts, item].slice(-MAX_TOASTS) }));
  return item.id;
}

function update(id: string, patch: Partial<ToastOptions>): void {
  useToastStore.setState((state) => ({
    toasts: state.toasts.map((t) => {
      if (t.id !== id) return t;
      const tone = patch.tone ?? t.tone;
      return {
        ...t,
        ...patch,
        tone,
        description: 'description' in patch ? patch.description : t.description,
        duration: patch.duration === undefined ? t.duration : patch.duration,
        progress: 'progress' in patch ? patch.progress : t.progress,
        action: 'action' in patch ? patch.action : t.action,
        revision: t.revision + 1,
      };
    }),
  }));
}

function dismiss(id: string): void {
  useToastStore.setState((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
}

export const toast = {
  show,
  update,
  dismiss,
  info: (title: string, description?: string) => show({ title, description, tone: 'info' }),
  success: (title: string, description?: string) => show({ title, description, tone: 'success' }),
  warning: (title: string, description?: string) => show({ title, description, tone: 'warning' }),
  /** Error toast; `error` may be an ApiError / Error / anything thrown. */
  error: (title: string, error?: unknown) =>
    show({ title, description: error === undefined ? undefined : errorMessage(error), tone: 'error' }),
};

export type ToastApi = typeof toast;

/** The toast API (stable identity; same object as the `toast` export). */
export function useToast(): ToastApi {
  return toast;
}
